import type { Rect } from "../../common/geometry.js";
import type { RenderContext } from "../render/RenderContext.js";
import type { Theme, VisualState } from "../themes/Theme.js";
import { ToggleControl, type ToggleOptions } from "./ToggleControl.js";

export interface CheckBoxOptions extends ToggleOptions {
  /** Allow the third, indeterminate state in the click cycle. */
  threeState?: boolean;
  indeterminate?: boolean;
}

export type CheckState = "unchecked" | "checked" | "indeterminate";

/** A two- or three-state check box with a caption. */
export class CheckBox extends ToggleControl {
  #indeterminate: boolean;
  threeState: boolean;

  constructor(options: CheckBoxOptions = {}) {
    super(options);
    this.threeState = options.threeState ?? false;
    this.#indeterminate = options.indeterminate ?? false;
  }

  get indeterminate(): boolean {
    return this.#indeterminate;
  }
  set indeterminate(v: boolean) {
    if (this.#indeterminate === v) return;
    this.#indeterminate = v;
    if (v) this.checkedValue = false;
    this.emit("change");
    this.invalidate();
  }

  get checkState(): CheckState {
    if (this.#indeterminate) return "indeterminate";
    return this.checked ? "checked" : "unchecked";
  }
  set checkState(v: CheckState) {
    const changed = this.checkState !== v;
    this.#indeterminate = v === "indeterminate";
    this.checkedValue = v === "checked";
    if (changed) {
      this.emit("change");
      this.invalidate();
    }
  }

  protected override setChecked(value: boolean): void {
    if (this.checked === value && !this.#indeterminate) return;
    this.#indeterminate = false;
    this.checkedValue = value;
    this.emit("change");
    this.invalidate();
  }

  /** unchecked -> checked -> (indeterminate) -> unchecked */
  protected override toggle(): void {
    if (!this.threeState) {
      this.checkState = this.checked ? "unchecked" : "checked";
      return;
    }
    this.checkState =
      this.checkState === "unchecked"
        ? "checked"
        : this.checkState === "checked"
          ? "indeterminate"
          : "unchecked";
  }

  protected override boxSize(theme: Theme): number {
    return theme.metrics.checkBoxSize;
  }

  protected override drawBox(rc: RenderContext, r: Rect, theme: Theme, s: VisualState): void {
    theme.drawCheckBox(rc, r, s);
  }

  override visualState(): VisualState {
    return { ...super.visualState(), indeterminate: this.#indeterminate };
  }
}
